import { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { X, Clock, Bell, Settings, Lock } from 'lucide-react';
import { useTranslation } from '@parentpath/i18n';
import { cn } from '@/lib/utils';
import { useLimits } from '@/hooks/useLimits';
import type {
  HospitalRule,
  NotificationMode,
  StatsUpdateInterval,
} from '../types';
import {
  HOSPITAL_RULES,
  NOTIFICATION_MODES,
  STATS_UPDATE_INTERVALS,
} from '../types';

interface SettingsSheetProps {
  isOpen: boolean;
  onClose: () => void;
  isInitialSetup?: boolean;
  currentSettings?: {
    hospital_rule: HospitalRule;
    notification_mode: NotificationMode;
    stats_update_interval: StatsUpdateInterval;
  } | null;
  onSave: (settings: {
    hospital_rule: HospitalRule;
    notification_mode: NotificationMode;
    stats_update_interval: StatsUpdateInterval;
  }) => Promise<boolean>;
}

export function SettingsSheet({
  isOpen,
  onClose,
  isInitialSetup = false,
  currentSettings,
  onSave,
}: SettingsSheetProps) {
  const { t } = useTranslation('contractionTimer');
  const { getLimit } = useLimits();
  const [hospitalRule, setHospitalRule] = useState<HospitalRule>('5-1-1');
  const [notificationMode, setNotificationMode] = useState<NotificationMode>('important');
  const [statsInterval, setStatsInterval] = useState<StatsUpdateInterval>(null);
  const [isSaving, setIsSaving] = useState(false);

  // Get allowed notification level based on tier limit
  // 'off' = only off allowed, 'important' = off + important, 'all' = all options
  const allowedNotificationLevel = getLimit<string>('contraction_partner_notifications');

  // Check if a mode is locked (premium)
  const isModeLocked = (mode: NotificationMode): boolean => {
    if (allowedNotificationLevel === 'all') return false;
    if (allowedNotificationLevel === 'important') return mode === 'all';
    if (allowedNotificationLevel === 'off') return mode !== 'off';
    return false;
  };

  // Initialize values when modal opens
  useEffect(() => {
    if (isOpen && currentSettings) {
      setHospitalRule(currentSettings.hospital_rule);
      setNotificationMode(currentSettings.notification_mode);
      setStatsInterval(currentSettings.stats_update_interval);
    } else if (isOpen && !currentSettings) {
      // Reset to defaults for initial setup
      // Start with 'off' so interval section is hidden until user explicitly chooses a mode
      setHospitalRule('5-1-1');
      setNotificationMode('off');
      setStatsInterval(null);
    }
  }, [isOpen, currentSettings]);

  if (!isOpen) return null;

  const handleSave = async () => {
    setIsSaving(true);
    try {
      const success = await onSave({
        hospital_rule: hospitalRule,
        notification_mode: notificationMode,
        stats_update_interval: statsInterval,
      });
      if (success) {
        onClose();
      }
    } finally {
      setIsSaving(false);
    }
  };

  const modalContent = (
    <>
      <div className="bottom-sheet-backdrop" onClick={isInitialSetup ? undefined : onClose} />
      <div className="bottom-sheet-container">
        <div className="bottom-sheet-modal">
          {/* Header */}
          <div className="bottom-sheet-header">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-pp-orange/20 rounded-full flex items-center justify-center">
                <Settings className="w-5 h-5 text-pp-orange" />
              </div>
              <div>
                <h2>{isInitialSetup ? t('settings.setupTitle') : t('settings.title')}</h2>
                {isInitialSetup && (
                  <p className="text-white/70 text-sm">{t('settings.description')}</p>
                )}
              </div>
            </div>
            {!isInitialSetup && (
              <button
                onClick={onClose}
                className="w-8 h-8 rounded-[5px] bg-white/20 flex items-center justify-center text-white hover:bg-white/30 transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            )}
          </div>

          {/* Content - scrollable */}
          <div className="bottom-sheet-content space-y-6">
            {/* Hospital Rule Section */}
            <div className="space-y-3">
              <div className="flex items-center gap-2 text-white">
                <Clock className="w-4 h-4 text-pp-orange" />
                <span className="font-medium">{t('settings.hospitalRule')}</span>
              </div>
              <p className="text-sm text-white/70">
                {t('settings.hospitalRuleHint')}
              </p>
              <div className="space-y-2">
                {HOSPITAL_RULES.map((rule) => (
                  <button
                    key={rule.name}
                    onClick={() => setHospitalRule(rule.name)}
                    className={cn(
                      'w-full p-3 rounded-[15px] text-left transition-all',
                      hospitalRule === rule.name
                        ? 'bg-pp-orange text-white'
                        : 'bg-white/10 text-white hover:bg-white/15'
                    )}
                  >
                    <div className="font-medium">{t(`hospitalRules.${rule.name}_label`)}</div>
                    <div className={cn(
                      'text-sm mt-0.5',
                      hospitalRule === rule.name ? 'text-white/80' : 'text-white/50'
                    )}>
                      {t(`hospitalRules.${rule.name}_description`)}
                    </div>
                  </button>
                ))}
              </div>
            </div>

            {/* Notification Mode Section */}
            <div className="space-y-3">
              <div className="flex items-center gap-2 text-white">
                <Bell className="w-4 h-4 text-pp-orange" />
                <span className="font-medium">{t('settings.partnerNotifications')}</span>
              </div>
              <p className="text-sm text-white/70">
                {t('settings.partnerNotificationsHint')}
              </p>
              <div className="space-y-2">
                {NOTIFICATION_MODES.map((mode) => {
                  const locked = isModeLocked(mode.value);
                  return (
                    <button
                      key={mode.value}
                      onClick={() => !locked && setNotificationMode(mode.value)}
                      disabled={locked}
                      className={cn(
                        'w-full p-3 rounded-[15px] text-left transition-all relative',
                        locked
                          ? 'bg-white/5 text-white/40 cursor-not-allowed'
                          : notificationMode === mode.value
                            ? 'bg-pp-orange text-white'
                            : 'bg-white/10 text-white hover:bg-white/15'
                      )}
                    >
                      <div className="flex items-center justify-between">
                        <div className="font-medium">{t(`notificationModes.${mode.value}_label`)}</div>
                        {locked && (
                          <div className="flex items-center gap-1 text-pp-orange text-xs">
                            <Lock className="w-3 h-3" />
                            {t('settings.premium')}
                          </div>
                        )}
                      </div>
                      <div className={cn(
                        'text-sm mt-0.5',
                        locked
                          ? 'text-white/30'
                          : notificationMode === mode.value
                            ? 'text-white/80'
                            : 'text-white/50'
                      )}>
                        {t(`notificationModes.${mode.value}_description`)}
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Stats Update Interval (only shown for 'important' mode) */}
            {notificationMode === 'important' && (
              <div className="space-y-3">
                <div className="flex items-center gap-2 text-white">
                  <Clock className="w-4 h-4 text-pp-orange" />
                  <span className="font-medium">{t('settings.statsUpdates')}</span>
                </div>
                <p className="text-sm text-white/70">
                  {t('settings.statsUpdatesHint')}
                </p>
                <div className="grid grid-cols-2 gap-2">
                  {STATS_UPDATE_INTERVALS.map((interval) => (
                    <button
                      key={interval.value ?? 'never'}
                      onClick={() => setStatsInterval(interval.value)}
                      className={cn(
                        'p-3 rounded-[15px] text-center transition-all font-medium',
                        statsInterval === interval.value
                          ? 'bg-pp-orange text-white'
                          : 'bg-white/10 text-white hover:bg-white/15'
                      )}
                    >
                      {t(`statsIntervals.${interval.value === null ? 'never' : interval.value === 30 ? 'every30' : 'every60'}`)}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Hint */}
            <p className="text-xs text-white/50">
              {isInitialSetup
                ? t('settings.settingsHint1')
                : t('settings.settingsHint2')}
            </p>
          </div>

          {/* Footer */}
          <div className="bottom-sheet-footer">
            {!isInitialSetup && (
              <button
                onClick={onClose}
                disabled={isSaving}
                className="flex-1 py-2.5 rounded-xl bg-white/20 border border-white/25 text-white font-medium hover:bg-white/30 disabled:opacity-50"
              >
                {t('settings.cancel')}
              </button>
            )}
            <button
              onClick={handleSave}
              disabled={isSaving}
              className={cn(
                'py-2.5 rounded-xl bg-pp-orange text-white font-medium hover:bg-pp-orange/90 disabled:opacity-50 flex items-center justify-center',
                isInitialSetup ? 'w-full' : 'flex-1'
              )}
            >
              {isSaving ? (
                <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
              ) : isInitialSetup ? (
                t('settings.startButton')
              ) : (
                t('settings.save')
              )}
            </button>
          </div>
        </div>
      </div>
    </>
  );

  return createPortal(modalContent, document.body);
}
